import { useEffect, useRef, useState } from "react";
import { useTranslation } from 'react-i18next';
import { ColumnDef, PaginationState } from "@tanstack/react-table";
import DataTableColumnHeader from "@/components/datatable/DataTableColumnHeader";
import { Loader, MoreVertical, PlusCircle, Calendar as CalendarIcon } from "lucide-react";
import { useQuery } from "react-query";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import DataTable from "@/components/datatable/Datatable";
import { toast } from "sonner";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import useModalState from "@/hooks/useModalState";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Input } from "@/components/ui/input";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { ScrollArea } from "@/components/ui/scroll-area";
import MultiSelect from "@/components/ui/common/MultiSelect";
import { useAuth } from "@/context/auth.context";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn, canPerformAdminActions } from "@/lib/utils";
import { format } from "date-fns";
import { DateRange } from "react-day-picker";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import * as z from "zod";
import useConfirmModal from "@/hooks/useConfirmModal";
import ConfirmModal from "@/components/modal/ConfirmModal";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import BulkImport from "@/components/modal/BulkImport";

const formSchema = z.object({
  firstName: z.string().min(2, "First name must be at least 2 characters"),
  lastName: z.string().min(2, "Last name must be at least 2 characters"),
  fullNames: z.string().min(2, "Full names must be at least 2 characters"),
  gender: z.enum(["Male", "Female", "Other"]),
  phone: z
    .string()
    .min(10, "Phone number must be at least 10 characters")
    .optional(),
  marriageStatus: z.enum(["Single", "Married", "Divorced", "Widowed"]),
  idNumber: z.string().min(1, "ID number is required"),
  country: z.string().min(1, "Country is required").optional(),
  currentAddress: z
    .string()
    .min(2, "Address must be at least 2 characters")
    .optional(),
  joinedAt: z.string().min(1, "Join date is required"),
  sourceOfIncome: z.enum([
    "Employment",
    "Business",
    "Farming",
    "Freelance",
    "Other",
  ]),
  districtId: z.string().min(1, "District is required"),
  groupIds: z.array(z.string()).optional(),
  branchId: z.string().min(1, "Branch is required"),
  memberCode: z.string().length(4, "Member code must be exactly 4 digits").regex(/^\d{4}$/, "Member code must be 4 digits"),
  role: z.enum(["President", "Secretary", "Accountant", "Member"], {
    required_error: "Role is required",
  }),
  email: z.string().email("Invalid email").optional(),
  password: z.string().min(6, "Password must be at least 6 characters").optional(),
});

function MemberForm({ isOpen, setIsOpen, refetch, record }) {
  // Track if leader role is already taken in selected group(s)
  const [roleConflict] = useState<string | null>(null);
  const { user } = useAuth();
  const { t } = useTranslation();

  // Extract group IDs helper function
  const extractGroupIds = (record) => {
    if (
      !record ||
      !record.groupMemberships ||
      !Array.isArray(record.groupMemberships)
    ) {
      return [];
    }
    return record.groupMemberships
      .map(
        (membership) =>
          membership.group?.id?.toString() || membership.groupId?.toString()
      )
      .filter(Boolean);
  };

  // Query for the user's group if not admin
  const { data: userGroup, refetch: refetchUserGroup } = useQuery(
    ["user-group"],
    async () => {
      const response = await api.get("/groups/my-group");
      return response.data;
    },
    {
      enabled: !canPerformAdminActions(user),
      staleTime: 0,
    }
  );

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    values: record
      ? {
        ...record,
        districtId: record?.branch?.district?.id?.toString() || "",
        branchId: record?.branch?.id?.toString() || "",
        groupIds: extractGroupIds(record),
        memberCode: record?.memberCode || "",
        gender: record?.gender || "Other",
        marriageStatus: record?.marriageStatus || "Single",
        sourceOfIncome: record?.sourceOfIncome || "Employment",
        country: record?.country || "Rwanda",
        currentAddress: record?.currentAddress || "",
        phone: record?.phone || "",
        email: record?.email || undefined,
        password: undefined,
      }
      : {
        firstName: "",
        lastName: "",
        fullNames: "",
        gender: "",
        phone: "",
        marriageStatus: "",
        idNumber: "",
        country: "",
        currentAddress: "",
        joinedAt: "",
        groupIds: [],
        districtId: "", // Will be set to Rubavu in useEffect
        branchId: "",
        memberCode: "",
        role: "Member",
        sourceOfIncome: "Employment",
      },
  });

  // Fetch districts
  const { data: districts } = useQuery(
    ["districts"],
    async () => {
      const { data } = await api.get("/districts");
      return data.results;
    },
    {
      staleTime: 60000, // 1 minute
    }
  );

  // Find Rubavu district ID
  const rubavuDistrict = districts?.find((d) => d.name?.toLowerCase() === "rubavu");
  const rubavuDistrictId = rubavuDistrict?.id?.toString();

  // Fetch branches based on selected district (only for non-Rubavu districts)
  const { data: districtWithBranches, refetch: refetchBranches } = useQuery(
    ["district-branches", form.watch("districtId")],
    async () => {
      const { data } = await api.get(`/districts/${form.watch("districtId")}`);
      return data;
    },
    {
      enabled: Boolean(form.watch("districtId")),
      staleTime: 30000, // 30 seconds
    }
  );

  // Sectors (branches) for the selected district
  const availableSectors = districtWithBranches?.branches || [];

  // Fetch groups based on selected branch
  const { data: groups, refetch: refetchGroups } = useQuery(
    [
      "groups",
      {
        branch: form.watch("branchId"),
      },
    ],
    async () => {
      const { data } = await api.get("/groups", {
        params: {
          filters: [
            {
              field: "branchId",
              operator: "eq",
              value: form.watch("branchId"),
            },
          ],
        },
      });
      return data.results;
    },
    {
      enabled: Boolean(form.watch("branchId")),
      staleTime: 30000, // 30 seconds
    }
  );

  // Set branch and group automatically when user is not admin
  useEffect(() => {
    if (isOpen) {
      // Refetch user group data when form opens
      if (!canPerformAdminActions(user)) {
        refetchUserGroup();
      }
    }
  }, [isOpen, user, refetchUserGroup]);

  // Effect to update form values when user group data is available or when editing a member
  useEffect(() => {
    if (isOpen) {
      if (!canPerformAdminActions(user)) {
        // Set district and branch IDs from user data for non-admins
        if (user?.branch?.district?.id) {
          form.setValue("districtId", user.branch.district.id.toString());
        }
        if (user?.branch?.id) {
          form.setValue("branchId", user.branch.id.toString());
        }
        // Set group ID when user group data is available
        if (userGroup?.data?.id) {
          form.setValue("groupIds", [userGroup.data.id.toString()]);
        } else if (user?.group?.id) {
          form.setValue("groupIds", [user.group.id.toString()]);
        }
      } else if (!record && rubavuDistrictId && canPerformAdminActions(user)) {
        // Set Rubavu as default district for new members (admin users only)
        if (!form.getValues("districtId")) {
          form.setValue("districtId", rubavuDistrictId);
        }
      }
      // If editing a member, set values from record and refetch branches/groups
      if (record) {
        // Set district and branch first, then refetch branches
        form.setValue("districtId", record?.branch?.district?.id?.toString() || "");
        form.setValue("branchId", record?.branch?.id?.toString() || "");
        refetchBranches();
        form.setValue("groupIds", extractGroupIds(record));
        refetchGroups();
      }
    }
  }, [isOpen, user, userGroup, form, record, refetchBranches, refetchGroups, rubavuDistrictId]);

  // Clear branch and group selections when district changes
  const watchedDistrictId = form.watch("districtId");
  const watchedBranchId = form.watch("branchId");
  const previousDistrictIdRef = useRef<string | null>(null);
  const previousBranchIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!watchedDistrictId) {
      form.setValue("branchId", "");
      form.setValue("groupIds", []);
      previousDistrictIdRef.current = null;
      return;
    }

    if (previousDistrictIdRef.current === null) {
      previousDistrictIdRef.current = watchedDistrictId;
      return;
    }

    if (previousDistrictIdRef.current === watchedDistrictId) {
      return;
    }

    previousDistrictIdRef.current = watchedDistrictId;
    form.setValue("branchId", "");
    form.setValue("groupIds", []);
    refetchBranches();
  }, [watchedDistrictId, form, refetchBranches]);

  // Clear group selections when branch changes
  useEffect(() => {
    if (!watchedBranchId) {
      form.setValue("groupIds", []);
      previousBranchIdRef.current = null;
      return;
    }

    if (previousBranchIdRef.current === null) {
      previousBranchIdRef.current = watchedBranchId;
      return;
    }

    if (previousBranchIdRef.current === watchedBranchId) {
      return;
    }

    previousBranchIdRef.current = watchedBranchId;
    form.setValue("groupIds", []);
    refetchGroups();
  }, [watchedBranchId, form, refetchGroups]);

  async function onSubmit(values: z.infer<typeof formSchema>) {
    // Only enforce for leader roles
    const leaderRoles = ["President", "Secretary", "Accountant"];
    if (leaderRoles.includes(values.role) && values.groupIds && values.groupIds.length > 0) {
      try {
        // Check for each group if the leader role is already assigned in the group entity
        const conflicts = [];
        for (const groupId of values.groupIds) {
          // Fetch group details
          const { data } = await api.get(`/groups/${groupId}`);
          let leaderId = null;
          if (values.role === "President") leaderId = data.president?.id;
          if (values.role === "Secretary") leaderId = data.secretary?.id;
          if (values.role === "Accountant") leaderId = data.accountant?.id;
          // If leader is set and not the current record, it's a conflict
          if (leaderId && (!record || leaderId !== record.id)) {
            conflicts.push(data.name || groupId);
          }
        }
        if (conflicts.length > 0) {
          form.setError("role", {
            type: "manual",
            message: `This group${conflicts.length > 1 ? 's' : ''} already ha${conflicts.length > 1 ? 've' : 's'} a ${values.role}.`,
          });
          return;
        }
      } catch (err) {
        toast.error("Failed to validate group leader role.");
        return;
      }
    }
    // Extract districtId from the form values before submission since the API doesn't expect it
    const q = record
      ? api.patch(`/members/${record.id}`, values)
      : api.post("/members", values);

    console.log("[Members] Submitting form", {
      mode: record ? "edit" : "create",
      payload: values,
    });

    return q
      .then(() => {
        refetch();
        toast.success(
          record ? t("messages.updatedSuccessfully") : t("messages.createdSuccessfully")
        );
        setIsOpen(false);
        form.reset();
      })
      .catch((e) => {
        console.error("[Members] Failed to submit member form", {
          mode: record ? "edit" : "create",
          payload: values,
          error: e,
          response: e?.response?.data,
        });
        toast.error(e.response?.data?.message || e.message);
        const errors = e?.response?.data?.meta?.errors || {};
        Object.keys(errors)?.forEach((field: any) => {
          form.setError(field, {
            message: errors[field],
          });
        });
      });
  }

  const { data: countries } = useQuery(["countries"], async () => {
    const data = await fetch("/countries.json")
      .then((e) => e.json())
      .then((e) => e.filter((e) => e.name.toLowerCase()))
      .then((e) => e.map((e) => ({ label: e.name, value: e.name })));
    return data;
  });

  const handleFormSubmit = form.handleSubmit(
    onSubmit,
    (errors) => {
      console.warn("[Members] Validation failed", errors);
      const firstError = Object.values(errors)[0];
      if (firstError && "message" in firstError && firstError?.message) {
        toast.error(firstError.message as string);
      } else {
        toast.error(t("messages.validationError") || "Please fix the highlighted fields.");
      }
    }
  );

  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetContent className="w-full flex flex-col !gap-0 sm:max-w-2xl p-0 md:max-w-3xl lg:max-w-2xl overflow-y-auto">
        <SheetHeader className="border-b px-4 py-2.5">
          <SheetTitle className="text-[15px]">
            {record ? t("pages.members.editMember") : t("pages.members.addMember")}
          </SheetTitle>
        </SheetHeader>
        <Form {...form}>
          <form
            onSubmit={handleFormSubmit}
            className="h-full flex flex-col"
          >
            <ScrollArea className="flex-1">
              <div className="space-y-8 py-3 px-4">
                {/* Location Information Section */}
                <div className="space-y-2">
                  <h3 className="text-sm font-medium text-gray-500 mb-2">
                    {t("forms.locationInformation")}
                  </h3>
                  <div className="grid grid-cols-2 gap-3">
                    <FormField
                      control={form.control}
                      name="memberCode"
                      render={({ field, fieldState }) => (
                        <FormItem>
                          <FormLabel>{t("forms.memberCode")} (4 {t("common.select")})</FormLabel>
                          <FormControl>
                            <Input
                              placeholder={t("forms.placeholderMemberCode")}
                              error={fieldState?.error?.message}
                              maxLength={4}
                              pattern="\d{4}"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="districtId"
                      render={({ field, fieldState }) => (
                        <FormItem>
                          <FormLabel>{t("forms.district")}</FormLabel>
                          <FormControl>
                            <Select
                              onValueChange={field.onChange}
                              value={field.value}
                              disabled={!canPerformAdminActions(user)} // Disable district selection for non-admin users
                            >
                              <FormControl>
                                <SelectTrigger error={fieldState?.error?.message}>
                                  <SelectValue placeholder={t("common.select") + " " + t("forms.district")} />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                {districts?.map((district) => (
                                  <SelectItem
                                    key={district.id}
                                    value={district.id?.toString()}
                                  >
                                    {district.name}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="branchId"
                      render={({ field, fieldState }) => (
                        <FormItem>
                          <FormLabel>{t("forms.sector")}</FormLabel>
                          <FormControl>
                            <Select
                              onValueChange={field.onChange}
                              value={field.value}
                              disabled={!form.watch("districtId") || !canPerformAdminActions(user)} // Disable branch selection if no district or non-admin
                            >
                              <FormControl>
                                <SelectTrigger error={fieldState?.error?.message}>
                                  <SelectValue placeholder={t("forms.placeholderSelectSector")} />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                {availableSectors.map((sector) => (
                                  <SelectItem
                                    key={sector.id}
                                    value={sector.id?.toString()}
                                  >
                                    {sector.name}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="groupIds"
                      render={({ field, fieldState }) => (
                        <FormItem className="col-span-2">
                          <FormLabel>{t("forms.group")}</FormLabel>
                          <FormControl>
                            <MultiSelect
                              disabled={!form.watch("branchId") || (!canPerformAdminActions(user) && Boolean(record))}
                              options={
                                groups?.map((g) => ({
                                  value: g.id.toString(),
                                  label: g.name,
                                })) || []
                              }
                              value={field.value || []}
                              onChange={field.onChange}
                              placeholder={t("forms.placeholderSelectGroups")}
                              error={fieldState?.error?.message}
                            />
                          </FormControl>
                          {fieldState?.error && (
                            <FormMessage>{fieldState.error.message}</FormMessage>
                          )}
                        </FormItem>
                      )}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  {/* Personal Information Section */}
                  <h3 className="text-sm font-medium text-gray-500 mb-2">
                    {t("forms.membersInformation")}
                  </h3>
                  <div className="grid grid-cols-2 gap-3">
                    <FormField
                      control={form.control}
                      name="firstName"
                      render={({ field, fieldState }) => (
                        <FormItem>
                          <FormLabel>{t("forms.firstName")}</FormLabel>
                          <FormControl>
                            <Input
                              placeholder={t("forms.placeholderEnterFirstName")}
                              error={fieldState?.error?.message}
                              {...field}
                            />
                          </FormControl>
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="lastName"
                      render={({ field, fieldState }) => (
                        <FormItem>
                          <FormLabel>{t("forms.lastName")}</FormLabel>
                          <FormControl>
                            <Input
                              placeholder={t("forms.placeholderEnterLastName")}
                              error={fieldState?.error?.message}
                              {...field}
                            />
                          </FormControl>
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="fullNames"
                      render={({ field, fieldState }) => (
                        <FormItem className="col-span-2">
                          <FormLabel>{t("forms.fullNames")}</FormLabel>
                          <FormControl>
                            <Input
                              placeholder={t("forms.placeholderEnterFullNames")}
                              error={fieldState?.error?.message}
                              {...field}
                            />
                          </FormControl>
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="role"
                      render={({ field, fieldState }) => (
                        <FormItem className="col-span-2">
                          <FormLabel>{t("forms.role")} *</FormLabel>
                          <FormControl>
                            <Select
                              onValueChange={field.onChange}
                              value={field.value}
                            >
                              <FormControl>
                                <SelectTrigger error={fieldState?.error?.message}>
                                  <SelectValue placeholder={t("forms.placeholderSelectRole")} />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                <SelectItem value="President">{t("forms.president")}</SelectItem>
                                <SelectItem value="Secretary">{t("forms.secretary")}</SelectItem>
                                <SelectItem value="Accountant">{t("forms.accountant")}</SelectItem>
                                <SelectItem value="Member">{t("common.member")}</SelectItem>
                              </SelectContent>
                            </Select>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    {form.watch('role') && form.watch('role') !== 'Member' && (
                      roleConflict ? (
                        <div className="col-span-2 text-red-600 text-sm font-medium mb-2">{roleConflict}</div>
                      ) : (
                        <>
                          <FormField
                            control={form.control}
                            name="email"
                            render={({ field, fieldState }) => (
                              <FormItem className="col-span-2">
                                <FormLabel>{t("common.email")}</FormLabel>
                                <FormControl>
                                  <Input
                                    type="email"
                                    placeholder={t("forms.placeholderEnterEmail")}
                                    error={fieldState?.error?.message}
                                    {...field}
                                  />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                          <FormField
                            control={form.control}
                            name="password"
                            render={({ field, fieldState }) => (
                              <FormItem className="col-span-2">
                                <FormLabel>{t("forms.password")}</FormLabel>
                                <FormControl>
                                  <Input
                                    type="password"
                                    placeholder={t("forms.placeholderEnterPassword")}
                                    error={fieldState?.error?.message}
                                    {...field}
                                  />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        </>
                      )
                    )}
                    <FormField
                      control={form.control}
                      name="gender"
                      render={({ field, fieldState }) => (
                        <FormItem>
                          <FormLabel>{t("forms.gender")}</FormLabel>
                          <FormControl>
                            <Select
                              onValueChange={field.onChange}
                              defaultValue={field.value}
                            >
                              <FormControl>
                                <SelectTrigger error={fieldState?.error?.message}>
                                  <SelectValue placeholder={t("forms.placeholderSelectGender")} />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                <SelectItem value="Male">{t("forms.male")}</SelectItem>
                                <SelectItem value="Female">{t("forms.female")}</SelectItem>
                                <SelectItem value="Other">{t("forms.other")}</SelectItem>
                              </SelectContent>
                            </Select>
                          </FormControl>
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="marriageStatus"
                      render={({ field, fieldState }) => (
                        <FormItem>
                          <FormLabel>{t("forms.marriageStatus")}</FormLabel>
                          <FormControl>
                            <Select
                              onValueChange={field.onChange}
                              defaultValue={field.value}
                            >
                              <FormControl>
                                <SelectTrigger error={fieldState?.error?.message}>
                                  <SelectValue placeholder={t("forms.placeholderSelectMarriageStatus")} />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                <SelectItem value="Single">{t("forms.single")}</SelectItem>
                                <SelectItem value="Married">{t("forms.married")}</SelectItem>
                                <SelectItem value="Divorced">{t("forms.divorced")}</SelectItem>
                                <SelectItem value="Widowed">{t("forms.widowed")}</SelectItem>
                              </SelectContent>
                            </Select>
                          </FormControl>
                        </FormItem>
                      )}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <FormField
                      control={form.control}
                      name="idNumber"
                      render={({ field, fieldState }) => (
                        <FormItem>
                          <FormLabel>{t("forms.idNumber")}</FormLabel>
                          <FormControl>
                            <Input
                              placeholder={t("forms.placeholderEnterIdNumber")}
                              error={fieldState?.error?.message}
                              type="number"
                              {...field}
                            />
                          </FormControl>
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="phone"
                      render={({ field, fieldState }) => (
                        <FormItem>
                          <FormLabel>{t("common.phone")}</FormLabel>
                          <FormControl>
                            <Input
                              placeholder={t("forms.placeholderEnterPhoneNumber")}
                              error={fieldState?.error?.message}
                              {...field}
                            />
                          </FormControl>
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="country"
                      render={({ field, fieldState }) => (
                        <FormItem>
                          <FormLabel>{t("forms.country")}</FormLabel>
                          <FormControl>
                            <Select
                              onValueChange={field.onChange}
                              defaultValue={field.value}
                            >
                              <FormControl>
                                <SelectTrigger error={fieldState?.error?.message}>
                                  <SelectValue placeholder={t("forms.placeholderSelectCountry")} />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                {countries?.map((e) => (
                                  <SelectItem key={e.value} value={e.value}>
                                    {e.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </FormControl>
                        </FormItem>
                      )}
                    />
                    <div className="col-span-2">
                      <FormField
                        control={form.control}
                        name="currentAddress"
                        render={({ field, fieldState }) => (
                          <FormItem>
                            <FormLabel>{t("forms.currentAddress")}</FormLabel>
                            <FormControl>
                              <Textarea
                                placeholder={t("forms.placeholderEnterCurrentAddress")}
                                error={fieldState?.error?.message}
                                {...field}
                              />
                            </FormControl>
                          </FormItem>
                        )}
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <FormField
                      control={form.control}
                      name="joinedAt"
                      render={({ field, fieldState }) => (
                        <FormItem>
                          <FormLabel>{t("forms.joinedAt")}</FormLabel>
                          <FormControl>
                            <Input
                              type="date"
                              error={fieldState?.error?.message}
                              {...field}
                            />
                          </FormControl>
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="sourceOfIncome"
                      render={({ field, fieldState }) => (
                        <FormItem>
                          <FormLabel>{t("forms.sourceOfIncome")}</FormLabel>
                          <FormControl>
                            <Select
                              onValueChange={field.onChange}
                              defaultValue={field.value}
                            >
                              <FormControl>
                                <SelectTrigger error={fieldState?.error?.message}>
                                  <SelectValue placeholder={t("forms.placeholderSelectSourceOfIncome")} />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                <SelectItem value="Employment">
                                  {t("forms.employment")}
                                </SelectItem>
                                <SelectItem value="Business">{t("forms.business")}</SelectItem>
                                <SelectItem value="Farming">{t("forms.farming")}</SelectItem>
                                <SelectItem value="Freelance">
                                  {t("forms.freelance")}
                                </SelectItem>
                                <SelectItem value="Other">{t("forms.otherIncome")}</SelectItem>
                              </SelectContent>
                            </Select>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </div>

              </div>
            </ScrollArea>
            <div className="mt-auto border-t px-3 py-2.5 flex gap-2 justify-end">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => {
                  setIsOpen(false);
                  form.reset();
                }}
              >
                Cancel
              </Button>
              <Button
                disabled={form.formState.isSubmitting}
                type="submit"
                size="sm"
              >
                {form.formState.isSubmitting && (
                  <Loader className="mr-2 h-4 w-4 text-white animate-spin" />
                )}
                {record ? t("common.update") : t("common.add")} {t("common.member")}
              </Button>
            </div>
          </form>
        </Form>
      </SheetContent>
    </Sheet>
  );
}


export default function Members() {
  const { t } = useTranslation();
  const [recordToEdit, setRecordToEdit] = useState(undefined);
  const [searchText, setSearchText] = useState("");
  const [columnFilters, setColumnFilters] = useState([]);
  const [dateRange, setDateRange] = useState<DateRange | undefined>({
    from: undefined,
    to: undefined,
  });

  // Set document title
  useEffect(() => {
    document.title = t("pages.members.title");
  }, [t]);

  const [sorting, setSorting] = useState([
    {
      id: "createdAt",
      desc: true,
    },
  ]);

  const [{ pageIndex, pageSize }, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 15,
  });

  const newRecordModal = useModalState();
  const confirmModal = useConfirmModal();
  const [selectedMember, setSelectedMember] = useState(null);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const { user } = useAuth();
  const [bulkImportOpen, setBulkImportOpen] = useState(false);
  // Sample row for template download
  const sampleMember = [
    { "Member Code": "0001", "Member Name": "BAHATI Innocent", "Leader Role": "member", "Phone": "0788123456", "Sector": "Rubavu", "Group": "Twisungane", "Current Savings": "5000 FRW", "Marital Status": "Single", "Source of Income": "Business", "Country": "Rwanda" },
    { "Member Code": "0002", "Member Name": "MUTESI Marie", "Leader Role": "member", "Phone": "0789234567", "Sector": "Rubavu", "Group": "Twisungane", "Current Savings": "3000 FRW", "Marital Status": "Married", "Source of Income": "Farming", "Country": "Rwanda" },
    { "Member Code": "0003", "Member Name": "HABIMANA Jean", "Leader Role": "member", "Phone": "0790345678", "Sector": "Rubavu", "Group": "Twisungane", "Current Savings": "4500 FRW", "Marital Status": "Single", "Source of Income": "Employment", "Country": "Rwanda" },
    { "Member Code": "0004", "Member Name": "NYARUABA Diane", "Leader Role": "member", "Phone": "0791456789", "Sector": "Rubavu", "Group": "Twisungane", "Current Savings": "2000 FRW", "Marital Status": "Single", "Source of Income": "Freelance", "Country": "Rwanda" },
    { "Member Code": "0005", "Member Name": "GATETE Paul", "Leader Role": "member", "Phone": "0792567890", "Sector": "Rubavu", "Group": "Twisungane", "Current Savings": "6000 FRW", "Marital Status": "Married", "Source of Income": "Business", "Country": "Rwanda" },
    { "Member Code": "0006", "Member Name": "MUKANDUTIYE Fiona", "Leader Role": "member", "Phone": "0793678901", "Sector": "Rubavu", "Group": "Twisungane", "Current Savings": "5500 FRW", "Marital Status": "Single", "Source of Income": "Employment", "Country": "Rwanda" },
    { "Member Code": "0007", "Member Name": "NSHIMIYIMANA Michel", "Leader Role": "member", "Phone": "0794789012", "Sector": "Rubavu", "Group": "Twisungane", "Current Savings": "3500 FRW", "Marital Status": "Married", "Source of Income": "Farming", "Country": "Rwanda" },
    { "Member Code": "0008", "Member Name": "UMUTONI Grace", "Leader Role": "member", "Phone": "0795890123", "Sector": "Rubavu", "Group": "Twisungane", "Current Savings": "4000 FRW", "Marital Status": "Widowed", "Source of Income": "Business", "Country": "Rwanda" },
    { "Member Code": "0009", "Member Name": "BIZIMUNGU Claude", "Leader Role": "member", "Phone": "0796901234", "Sector": "Rubavu", "Group": "Twisungane", "Current Savings": "5200 FRW", "Marital Status": "Single", "Source of Income": "Employment", "Country": "Rwanda" },
    { "Member Code": "0010", "Member Name": "KAREMERA Alice", "Leader Role": "member", "Phone": "0797012345", "Sector": "Rubavu", "Group": "Twisungane", "Current Savings": "2800 FRW", "Marital Status": "Married", "Source of Income": "Farming", "Country": "Rwanda" },
    { "Member Code": "0011", "Member Name": "NKRUMAH David", "Leader Role": "member", "Phone": "0788123457", "Sector": "Rubavu", "Group": "Twisungane", "Current Savings": "6500 FRW", "Marital Status": "Single", "Source of Income": "Business", "Country": "Rwanda" },
    { "Member Code": "0012", "Member Name": "UWINGABIRE Celine", "Leader Role": "member", "Phone": "0789234568", "Sector": "Rubavu", "Group": "Twisungane", "Current Savings": "4200 FRW", "Marital Status": "Single", "Source of Income": "Freelance", "Country": "Rwanda" },
    { "Member Code": "0013", "Member Name": "MUTABAZI Frederick", "Leader Role": "member", "Phone": "0790345679", "Sector": "Rubavu", "Group": "Twisungane", "Current Savings": "3800 FRW", "Marital Status": "Married", "Source of Income": "Employment", "Country": "Rwanda" },
    { "Member Code": "0014", "Member Name": "ILUNGA Patricia", "Leader Role": "member", "Phone": "0791456790", "Sector": "Rubavu", "Group": "Twisungane", "Current Savings": "5100 FRW", "Marital Status": "Single", "Source of Income": "Business", "Country": "Rwanda" },
    { "Member Code": "0015", "Member Name": "NSABIYUMVA Eric", "Leader Role": "member", "Phone": "0792567891", "Sector": "Rubavu", "Group": "Twisungane", "Current Savings": "2500 FRW", "Marital Status": "Married", "Source of Income": "Farming", "Country": "Rwanda" },
    { "Member Code": "0016", "Member Name": "TURINAWE Olive", "Leader Role": "member", "Phone": "0793678902", "Sector": "Rubavu", "Group": "Twisungane", "Current Savings": "4700 FRW", "Marital Status": "Widowed", "Source of Income": "Employment", "Country": "Rwanda" },
    { "Member Code": "0017", "Member Name": "MUGISHA Stephen", "Leader Role": "member", "Phone": "0794789013", "Sector": "Rubavu", "Group": "Twisungane", "Current Savings": "5900 FRW", "Marital Status": "Single", "Source of Income": "Business", "Country": "Rwanda" },
    { "Member Code": "0018", "Member Name": "NYIRABIKALI Rose", "Leader Role": "member", "Phone": "0795890124", "Sector": "Rubavu", "Group": "Twisungane", "Current Savings": "3300 FRW", "Marital Status": "Married", "Source of Income": "Freelance", "Country": "Rwanda" },
    { "Member Code": "0019", "Member Name": "KAMUNYU Benjamin", "Leader Role": "member", "Phone": "0796901235", "Sector": "Rubavu", "Group": "Twisungane", "Current Savings": "4600 FRW", "Marital Status": "Single", "Source of Income": "Employment", "Country": "Rwanda" },
    { "Member Code": "0020", "Member Name": "HABIYAREMYE Beatrice", "Leader Role": "member", "Phone": "0797012346", "Sector": "Rubavu", "Group": "Twisungane", "Current Savings": "2900 FRW", "Marital Status": "Single", "Source of Income": "Farming", "Country": "Rwanda" },
    { "Member Code": "0021", "Member Name": "NIYIGENA Joseph", "Leader Role": "member", "Phone": "0788123458", "Sector": "Rubavu", "Group": "Twisungane", "Current Savings": "5400 FRW", "Marital Status": "Married", "Source of Income": "Business", "Country": "Rwanda" },
    { "Member Code": "0022", "Member Name": "MBABAZI Sandra", "Leader Role": "member", "Phone": "0789234569", "Sector": "Rubavu", "Group": "Twisungane", "Current Savings": "3100 FRW", "Marital Status": "Single", "Source of Income": "Employment", "Country": "Rwanda" },
    { "Member Code": "0023", "Member Name": "RUGAMBA Moses", "Leader Role": "member", "Phone": "0790345680", "Sector": "Rubavu", "Group": "Twisungane", "Current Savings": "4100 FRW", "Marital Status": "Divorced", "Source of Income": "Farming", "Country": "Rwanda" },
    { "Member Code": "0024", "Member Name": "UZAMUKUNDA Lucy", "Leader Role": "member", "Phone": "0791456791", "Sector": "Rubavu", "Group": "Twisungane", "Current Savings": "6200 FRW", "Marital Status": "Single", "Source of Income": "Freelance", "Country": "Rwanda" },
    { "Member Code": "0025", "Member Name": "MUTUA Daniel", "Leader Role": "member", "Phone": "0792567892", "Sector": "Rubavu", "Group": "Twisungane", "Current Savings": "5000 FRW", "Marital Status": "Married", "Source of Income": "Business", "Country": "Rwanda" },
    { "Member Code": "0026", "Member Name": "MUNEZA Nicole", "Leader Role": "member", "Phone": "0793678903", "Sector": "Rubavu", "Group": "Twisungane", "Current Savings": "3600 FRW", "Marital Status": "Single", "Source of Income": "Employment", "Country": "Rwanda" },
    { "Member Code": "0027", "Member Name": "GASANA Vincent", "Leader Role": "member", "Phone": "0794789014", "Sector": "Rubavu", "Group": "Twisungane", "Current Savings": "2300 FRW", "Marital Status": "Widowed", "Source of Income": "Farming", "Country": "Rwanda" },
    { "Member Code": "0028", "Member Name": "KAGABA Deborah", "Leader Role": "member", "Phone": "0795890125", "Sector": "Rubavu", "Group": "Twisungane", "Current Savings": "4800 FRW", "Marital Status": "Single", "Source of Income": "Business", "Country": "Rwanda" },
    { "Member Code": "0029", "Member Name": "MUHIGIRWA Robert", "Leader Role": "member", "Phone": "0796901236", "Sector": "Rubavu", "Group": "Twisungane", "Current Savings": "5300 FRW", "Marital Status": "Married", "Source of Income": "Employment", "Country": "Rwanda" },
    { "Member Code": "0030", "Member Name": "UWERA Helen", "Leader Role": "member", "Phone": "0797012347", "Sector": "Rubavu", "Group": "Twisungane", "Current Savings": "2700 FRW", "Marital Status": "Single", "Source of Income": "Freelance", "Country": "Rwanda" },
    { "Member Code": "0031", "Member Name": "KAYIGAMBA Francis", "Leader Role": "member", "Phone": "0788123459", "Sector": "Rubavu", "Group": "Twisungane", "Current Savings": "5600 FRW", "Marital Status": "Single", "Source of Income": "Farming", "Country": "Rwanda" },
    { "Member Code": "0032", "Member Name": "NKURUNZIZA Theresa", "Leader Role": "member", "Phone": "0789234570", "Sector": "Rubavu", "Group": "Twisungane", "Current Savings": "3900 FRW", "Marital Status": "Married", "Source of Income": "Business", "Country": "Rwanda" },
    { "Member Code": "0033", "Member Name": "GATABAZI Patrick", "Leader Role": "member", "Phone": "0790345681", "Sector": "Rubavu", "Group": "Twisungane", "Current Savings": "4300 FRW", "Marital Status": "Single", "Source of Income": "Employment", "Country": "Rwanda" },
    { "Member Code": "0034", "Member Name": "RUFUGUMA Janet", "Leader Role": "member", "Phone": "0791456792", "Sector": "Rubavu", "Group": "Twisungane", "Current Savings": "2400 FRW", "Marital Status": "Divorced", "Source of Income": "Freelance", "Country": "Rwanda" },
    { "Member Code": "0035", "Member Name": "MINANI George", "Leader Role": "member", "Phone": "0792567893", "Sector": "Rubavu", "Group": "Twisungane", "Current Savings": "5700 FRW", "Marital Status": "Married", "Source of Income": "Farming", "Country": "Rwanda" },
    { "Member Code": "0036", "Member Name": "KARANGWA Susan", "Leader Role": "member", "Phone": "0793678904", "Sector": "Rubavu", "Group": "Twisungane", "Current Savings": "3200 FRW", "Marital Status": "Single", "Source of Income": "Business", "Country": "Rwanda" },
    { "Member Code": "0037", "Member Name": "NSENGA Charles", "Leader Role": "member", "Phone": "0794789015", "Sector": "Rubavu", "Group": "Twisungane", "Current Savings": "4900 FRW", "Marital Status": "Single", "Source of Income": "Employment", "Country": "Rwanda" },
    { "Member Code": "0038", "Member Name": "IBITURO Miriam", "Leader Role": "member", "Phone": "0795890126", "Sector": "Rubavu", "Group": "Twisungane", "Current Savings": "5800 FRW", "Marital Status": "Widowed", "Source of Income": "Freelance", "Country": "Rwanda" },
    { "Member Code": "0039", "Member Name": "KABAJIRA Jonathan", "Leader Role": "member", "Phone": "0796901237", "Sector": "Rubavu", "Group": "Twisungane", "Current Savings": "2600 FRW", "Marital Status": "Married", "Source of Income": "Farming", "Country": "Rwanda" },
    { "Member Code": "0040", "Member Name": "UWACU Elizabeth", "Leader Role": "member", "Phone": "0797012348", "Sector": "Rubavu", "Group": "Twisungane", "Current Savings": "4400 FRW", "Marital Status": "Single", "Source of Income": "Business", "Country": "Rwanda" },
    { "Member Code": "0041", "Member Name": "NDAYISABA Thomas", "Leader Role": "member", "Phone": "0788123460", "Sector": "Rubavu", "Group": "Twisungane", "Current Savings": "3400 FRW", "Marital Status": "Single", "Source of Income": "Employment", "Country": "Rwanda" },
    { "Member Code": "0042", "Member Name": "MWIZIGIRA Judith", "Leader Role": "member", "Phone": "0789234571", "Sector": "Rubavu", "Group": "Twisungane", "Current Savings": "5050 FRW", "Marital Status": "Married", "Source of Income": "Freelance", "Country": "Rwanda" },
    { "Member Code": "0043", "Member Name": "NKUBITO Samuel", "Leader Role": "member", "Phone": "0790345682", "Sector": "Rubavu", "Group": "Twisungane", "Current Savings": "2800 FRW", "Marital Status": "Single", "Source of Income": "Farming", "Country": "Rwanda" },
    { "Member Code": "0044", "Member Name": "KAMANZI Rachel", "Leader Role": "member", "Phone": "0791456793", "Sector": "Rubavu", "Group": "Twisungane", "Current Savings": "4500 FRW", "Marital Status": "Divorced", "Source of Income": "Business", "Country": "Rwanda" },
    { "Member Code": "0045", "Member Name": "USIFIKAPO Martin", "Leader Role": "member", "Phone": "0792567894", "Sector": "Rubavu", "Group": "Twisungane", "Current Savings": "5900 FRW", "Marital Status": "Married", "Source of Income": "Employment", "Country": "Rwanda" },
    { "Member Code": "0046", "Member Name": "NYIRAMBITUYE Victoria", "Leader Role": "member", "Phone": "0793678905", "Sector": "Rubavu", "Group": "Twisungane", "Current Savings": "3300 FRW", "Marital Status": "Single", "Source of Income": "Freelance", "Country": "Rwanda" },
    { "Member Code": "0047", "Member Name": "MUNYERIZIKI Albert", "Leader Role": "member", "Phone": "0794789016", "Sector": "Rubavu", "Group": "Twisungane", "Current Savings": "4700 FRW", "Marital Status": "Widowed", "Source of Income": "Farming", "Country": "Rwanda" },
    { "Member Code": "0048", "Member Name": "KABASEKE Wendy", "Leader Role": "member", "Phone": "0795890127", "Sector": "Rubavu", "Group": "Twisungane", "Current Savings": "2900 FRW", "Marital Status": "Single", "Source of Income": "Business", "Country": "Rwanda" },
    { "Member Code": "0049", "Member Name": "RUGAMBA Isaiah", "Leader Role": "member", "Phone": "0796901238", "Sector": "Rubavu", "Group": "Twisungane", "Current Savings": "5100 FRW", "Marital Status": "Married", "Source of Income": "Employment", "Country": "Rwanda" },
    { "Member Code": "0050", "Member Name": "MPABIRE Sharon", "Leader Role": "member", "Phone": "0797012349", "Sector": "Rubavu", "Group": "Twisungane", "Current Savings": "3700 FRW", "Marital Status": "Single", "Source of Income": "Freelance", "Country": "Rwanda" },
    { "Member Code": "0051", "Member Name": "NGARAMBE Richard", "Leader Role": "member", "Phone": "0788123461", "Sector": "Rubavu", "Group": "Twisungane", "Current Savings": "4600 FRW", "Marital Status": "Single", "Source of Income": "Farming", "Country": "Rwanda" },
    { "Member Code": "0052", "Member Name": "HABIYAREMYE Catherine", "Leader Role": "member", "Phone": "0789234572", "Sector": "Rubavu", "Group": "Twisungane", "Current Savings": "6000 FRW", "Marital Status": "Married", "Source of Income": "Business", "Country": "Rwanda" },
    { "Member Code": "0053", "Member Name": "NKUINZIRA Lawrence", "Leader Role": "member", "Phone": "0790345683", "Sector": "Rubavu", "Group": "Twisungane", "Current Savings": "2700 FRW", "Marital Status": "Divorced", "Source of Income": "Employment", "Country": "Rwanda" },
    { "Member Code": "0054", "Member Name": "KAYIBANDA Martha", "Leader Role": "member", "Phone": "0791456794", "Sector": "Rubavu", "Group": "Twisungane", "Current Savings": "5200 FRW", "Marital Status": "Single", "Source of Income": "Freelance", "Country": "Rwanda" },
    { "Member Code": "0055", "Member Name": "GAHIRE Simon", "Leader Role": "member", "Phone": "0792567895", "Sector": "Rubavu", "Group": "Twisungane", "Current Savings": "3500 FRW", "Marital Status": "Widowed", "Source of Income": "Farming", "Country": "Rwanda" },
    { "Member Code": "0056", "Member Name": "MURENZI Francoise", "Leader Role": "member", "Phone": "0793678906", "Sector": "Rubavu", "Group": "Twisungane", "Current Savings": "4800 FRW", "Marital Status": "Married", "Source of Income": "Business", "Country": "Rwanda" },
    { "Member Code": "0057", "Member Name": "NSENGIYUMVA Gerald", "Leader Role": "member", "Phone": "0794789017", "Sector": "Rubavu", "Group": "Twisungane", "Current Savings": "2300 FRW", "Marital Status": "Single", "Source of Income": "Employment", "Country": "Rwanda" },
    { "Member Code": "0058", "Member Name": "IRIBAGIZA Anna", "Leader Role": "member", "Phone": "0795890128", "Sector": "Rubavu", "Group": "Twisungane", "Current Savings": "5300 FRW", "Marital Status": "Single", "Source of Income": "Freelance", "Country": "Rwanda" },
    { "Member Code": "0059", "Member Name": "MUNYESHYAKA Emmanuel", "Leader Role": "member", "Phone": "0796901239", "Sector": "Rubavu", "Group": "Twisungane", "Current Savings": "3800 FRW", "Marital Status": "Married", "Source of Income": "Farming", "Country": "Rwanda" },
    { "Member Code": "0060", "Member Name": "NYIRITSHIMANYA Doreen", "Leader Role": "member", "Phone": "0797012350", "Sector": "Rubavu", "Group": "Twisungane", "Current Savings": "4400 FRW", "Marital Status": "Divorced", "Source of Income": "Business", "Country": "Rwanda" },
    { "Member Code": "0061", "Member Name": "MUVUNYI Kenneth", "Leader Role": "member", "Phone": "0788123462", "Sector": "Rubavu", "Group": "Twisungane", "Current Savings": "5600 FRW", "Marital Status": "Widowed", "Source of Income": "Employment", "Country": "Rwanda" },
    { "Member Code": "0062", "Member Name": "HABIMANA Sylvia", "Leader Role": "member", "Phone": "0789234573", "Sector": "Rubavu", "Group": "Twisungane", "Current Savings": "2500 FRW", "Marital Status": "Single", "Source of Income": "Freelance", "Country": "Rwanda" },
    { "Member Code": "0063", "Member Name": "BIKINDI Lucas", "Leader Role": "member", "Phone": "0790345684", "Sector": "Rubavu", "Group": "Twisungane", "Current Savings": "4900 FRW", "Marital Status": "Married", "Source of Income": "Farming", "Country": "Rwanda" },
    { "Member Code": "0064", "Member Name": "MAGAMBI Florence", "Leader Role": "member", "Phone": "0791456795", "Sector": "Rubavu", "Group": "Twisungane", "Current Savings": "3200 FRW", "Marital Status": "Single", "Source of Income": "Business", "Country": "Rwanda" },
    { "Member Code": "0065", "Member Name": "UWITONZE Arthur", "Leader Role": "member", "Phone": "0792567896", "Sector": "Rubavu", "Group": "Twisungane", "Current Savings": "5500 FRW", "Marital Status": "Single", "Source of Income": "Employment", "Country": "Rwanda" },
    { "Member Code": "0066", "Member Name": "KARANGWA Valerie", "Leader Role": "member", "Phone": "0793678907", "Sector": "Rubavu", "Group": "Twisungane", "Current Savings": "2800 FRW", "Marital Status": "Married", "Source of Income": "Freelance", "Country": "Rwanda" },
    { "Member Code": "0067", "Member Name": "KABAHONDA Peter", "Leader Role": "member", "Phone": "0794789018", "Sector": "Rubavu", "Group": "Twisungane", "Current Savings": "4700 FRW", "Marital Status": "Divorced", "Source of Income": "Farming", "Country": "Rwanda" },
    { "Member Code": "0068", "Member Name": "UWORWIRE Beverly", "Leader Role": "member", "Phone": "0795890129", "Sector": "Rubavu", "Group": "Twisungane", "Current Savings": "5100 FRW", "Marital Status": "Widowed", "Source of Income": "Business", "Country": "Rwanda" },
    { "Member Code": "0069", "Member Name": "NIYONKURU Jackson", "Leader Role": "member", "Phone": "0796901240", "Sector": "Rubavu", "Group": "Twisungane", "Current Savings": "3600 FRW", "Marital Status": "Single", "Source of Income": "Employment", "Country": "Rwanda" },
    { "Member Code": "0070", "Member Name": "NYIRANEZA Lydia", "Leader Role": "member", "Phone": "0797012351", "Sector": "Rubavu", "Group": "Twisungane", "Current Savings": "5000 FRW", "Marital Status": "Married", "Source of Income": "Freelance", "Country": "Rwanda" },
    { "Member Code": "0071", "Member Name": "HABIMUNZU Andrew", "Leader Role": "member", "Phone": "0788123463", "Sector": "Rubavu", "Group": "Twisungane", "Current Savings": "2900 FRW", "Marital Status": "Single", "Source of Income": "Farming", "Country": "Rwanda" },
    { "Member Code": "0072", "Member Name": "MUTABAZI Linda", "Leader Role": "member", "Phone": "0789234574", "Sector": "Rubavu", "Group": "Twisungane", "Current Savings": "4600 FRW", "Marital Status": "Single", "Source of Income": "Business", "Country": "Rwanda" },
    { "Member Code": "0073", "Member Name": "NSEREKO Winston", "Leader Role": "member", "Phone": "0790345685", "Sector": "Rubavu", "Group": "Twisungane", "Current Savings": "5900 FRW", "Marital Status": "Married", "Source of Income": "Employment", "Country": "Rwanda" },
    { "Member Code": "0074", "Member Name": "KAMANZI Monika", "Leader Role": "member", "Phone": "0791456796", "Sector": "Rubavu", "Group": "Twisungane", "Current Savings": "3100 FRW", "Marital Status": "Divorced", "Source of Income": "Freelance", "Country": "Rwanda" },
    { "Member Code": "0075", "Member Name": "MUREKEZI Gregory", "Leader Role": "member", "Phone": "0792567897", "Sector": "Rubavu", "Group": "Twisungane", "Current Savings": "5200 FRW", "Marital Status": "Widowed", "Source of Income": "Farming", "Country": "Rwanda" },
    { "Member Code": "0076", "Member Name": "NYIRAMUSUHUKE Tessa", "Leader Role": "member", "Phone": "0793678908", "Sector": "Rubavu", "Group": "Twisungane", "Current Savings": "2700 FRW", "Marital Status": "Single", "Source of Income": "Business", "Country": "Rwanda" },
    { "Member Code": "0077", "Member Name": "GATSINZI Mark", "Leader Role": "member", "Phone": "0794789019", "Sector": "Rubavu", "Group": "Twisungane", "Current Savings": "4500 FRW", "Marital Status": "Married", "Source of Income": "Employment", "Country": "Rwanda" },
    { "Member Code": "0078", "Member Name": "GATERA Roxanne", "Leader Role": "member", "Phone": "0795890130", "Sector": "Rubavu", "Group": "Twisungane", "Current Savings": "5800 FRW", "Marital Status": "Single", "Source of Income": "Freelance", "Country": "Rwanda" },
    { "Member Code": "0079", "Member Name": "HABIYAMBERE Richard", "Leader Role": "member", "Phone": "0796901241", "Sector": "Rubavu", "Group": "Twisungane", "Current Savings": "3400 FRW", "Marital Status": "Single", "Source of Income": "Farming", "Country": "Rwanda" },
    { "Member Code": "0080", "Member Name": "NYIRAMURAMUTSE Esther", "Leader Role": "member", "Phone": "0797012352", "Sector": "Rubavu", "Group": "Twisungane", "Current Savings": "5100 FRW", "Marital Status": "Married", "Source of Income": "Business", "Country": "Rwanda" },
    { "Member Code": "0081", "Member Name": "NKURUNZIZA Anthony", "Leader Role": "member", "Phone": "0788123464", "Sector": "Rubavu", "Group": "Twisungane", "Current Savings": "2600 FRW", "Marital Status": "Divorced", "Source of Income": "Employment", "Country": "Rwanda" },
    { "Member Code": "0082", "Member Name": "HABIMANA Priscilla", "Leader Role": "member", "Phone": "0789234575", "Sector": "Rubavu", "Group": "Twisungane", "Current Savings": "4800 FRW", "Marital Status": "Widowed", "Source of Income": "Freelance", "Country": "Rwanda" },
    { "Member Code": "0083", "Member Name": "MUNYENZI Isaiah", "Leader Role": "member", "Phone": "0790345686", "Sector": "Rubavu", "Group": "Twisungane", "Current Savings": "5300 FRW", "Marital Status": "Single", "Source of Income": "Farming", "Country": "Rwanda" },
    { "Member Code": "0084", "Member Name": "NKURUNGIZA Fiona", "Leader Role": "member", "Phone": "0791456797", "Sector": "Rubavu", "Group": "Twisungane", "Current Savings": "3000 FRW", "Marital Status": "Married", "Source of Income": "Business", "Country": "Rwanda" },
    { "Member Code": "0085", "Member Name": "KARUHIMBI Joel", "Leader Role": "member", "Phone": "0792567898", "Sector": "Rubavu", "Group": "Twisungane", "Current Savings": "5600 FRW", "Marital Status": "Single", "Source of Income": "Employment", "Country": "Rwanda" },
    { "Member Code": "0086", "Member Name": "MUKARUGWIRO Georgiana", "Leader Role": "member", "Phone": "0793678909", "Sector": "Rubavu", "Group": "Twisungane", "Current Savings": "2400 FRW", "Marital Status": "Divorced", "Source of Income": "Freelance", "Country": "Rwanda" },
    { "Member Code": "0087", "Member Name": "NSHUTI Olivier", "Leader Role": "member", "Phone": "0794789020", "Sector": "Rubavu", "Group": "Twisungane", "Current Savings": "4900 FRW", "Marital Status": "Widowed", "Source of Income": "Farming", "Country": "Rwanda" },
    { "Member Code": "0088", "Member Name": "HABIMANA Sylvie", "Leader Role": "member", "Phone": "0795890131", "Sector": "Rubavu", "Group": "Twisungane", "Current Savings": "5100 FRW", "Marital Status": "Single", "Source of Income": "Business", "Country": "Rwanda" },
    { "Member Code": "0089", "Member Name": "MUTABURIKA Oscar", "Leader Role": "member", "Phone": "0796901242", "Sector": "Rubavu", "Group": "Twisungane", "Current Savings": "3300 FRW", "Marital Status": "Married", "Source of Income": "Employment", "Country": "Rwanda" },
    { "Member Code": "0090", "Member Name": "NYIRAMAHORO Josephine", "Leader Role": "member", "Phone": "0797012353", "Sector": "Rubavu", "Group": "Twisungane", "Current Savings": "5700 FRW", "Marital Status": "Single", "Source of Income": "Freelance", "Country": "Rwanda" },
    { "Member Code": "0091", "Member Name": "KAYISIRE Edwin", "Leader Role": "member", "Phone": "0788123465", "Sector": "Rubavu", "Group": "Twisungane", "Current Savings": "2800 FRW", "Marital Status": "Single", "Source of Income": "Farming", "Country": "Rwanda" },
    { "Member Code": "0092", "Member Name": "MWEZI Therese", "Leader Role": "member", "Phone": "0789234576", "Sector": "Rubavu", "Group": "Twisungane", "Current Savings": "5000 FRW", "Marital Status": "Married", "Source of Income": "Business", "Country": "Rwanda" },
    { "Member Code": "0093", "Member Name": "GAHUTU Leonard", "Leader Role": "member", "Phone": "0790345687", "Sector": "Rubavu", "Group": "Twisungane", "Current Savings": "3700 FRW", "Marital Status": "Divorced", "Source of Income": "Employment", "Country": "Rwanda" },
    { "Member Code": "0094", "Member Name": "NZASABIMANA Carole", "Leader Role": "member", "Phone": "0791456798", "Sector": "Rubavu", "Group": "Twisungane", "Current Savings": "5400 FRW", "Marital Status": "Widowed", "Source of Income": "Freelance", "Country": "Rwanda" },
    { "Member Code": "0095", "Member Name": "MUHIRE Ferdinand", "Leader Role": "member", "Phone": "0792567899", "Sector": "Rubavu", "Group": "Twisungane", "Current Savings": "2300 FRW", "Marital Status": "Single", "Source of Income": "Farming", "Country": "Rwanda" },
    { "Member Code": "0096", "Member Name": "MUREKEZA Patricia", "Leader Role": "member", "Phone": "0793678910", "Sector": "Rubavu", "Group": "Twisungane", "Current Savings": "5100 FRW", "Marital Status": "Married", "Source of Income": "Business", "Country": "Rwanda" },
    { "Member Code": "0097", "Member Name": "NSANZIMANA Paul", "Leader Role": "member", "Phone": "0794789021", "Sector": "Rubavu", "Group": "Twisungane", "Current Savings": "4200 FRW", "Marital Status": "Single", "Source of Income": "Employment", "Country": "Rwanda" },
    { "Member Code": "0098", "Member Name": "NYIRAMANA Vivian", "Leader Role": "member", "Phone": "0795890132", "Sector": "Rubavu", "Group": "Twisungane", "Current Savings": "5800 FRW", "Marital Status": "Single", "Source of Income": "Freelance", "Country": "Rwanda" },
    { "Member Code": "0099", "Member Name": "NSHOGOZA William", "Leader Role": "member", "Phone": "0796901243", "Sector": "Rubavu", "Group": "Twisungane", "Current Savings": "3100 FRW", "Marital Status": "Married", "Source of Income": "Farming", "Country": "Rwanda" },
    { "Member Code": "0100", "Member Name": "HABIMANA Yvette", "Leader Role": "member", "Phone": "0797012354", "Sector": "Rubavu", "Group": "Twisungane", "Current Savings": "5200 FRW", "Marital Status": "Divorced", "Source of Income": "Business", "Country": "Rwanda" }
  ];

  // Add separate queries for filter options
  const { data: branches = [] } = useQuery(["branches"], async () => {
    const { data } = await api.get("/branches");
    return data.results.map(branch => ({
      label: branch.name,
      value: branch.name
    }));
  });


  // Add gender options
  const genderOptions = [
    { label: "Male", value: "Male" },
    { label: "Female", value: "Female" },
    { label: "Other", value: "Other" }
  ];

  // Add marriage status options
  const marriageStatusOptions = [
    { label: "Single", value: "Single" },
    { label: "Married", value: "Married" },
    { label: "Divorced", value: "Divorced" },
    { label: "Widowed", value: "Widowed" }
  ];

  // Add source of income options
  const sourceOfIncomeOptions = [
    { label: "Employment", value: "Employment" },
    { label: "Business", value: "Business" },
    { label: "Farming", value: "Farming" },
    { label: "Freelance", value: "Freelance" },
    { label: "Other", value: "Other" }
  ];

  // Add a function to handle date range changes
  const handleDateRangeChange = (range: DateRange | undefined) => {
    setDateRange(range);
    if (range?.from && range?.to) {
      setColumnFilters(prev => {
        const otherFilters = prev.filter(f => f.id !== "createdAt");
        return [
          ...otherFilters,
          {
            id: "createdAt",
            value: [range.from, range.to]
          }
        ];
      });
    } else {
      setColumnFilters(prev => prev.filter(f => f.id !== "createdAt"));
    }
  };

  const recordsQuery = useQuery({
    queryKey: [
      "members",
      {
        search: searchText,
        filter: columnFilters,
        sort: sorting,
        pageIndex,
        pageSize,
      },
    ],
    keepPreviousData: true,
    queryFn: async () => {
      console.log("Current filters:", columnFilters);

      const params = {
        page_size: pageSize,
        page: pageIndex + 1,
        ...(searchText && { search: searchText }),
        ...(columnFilters.length > 0 && {
          filters: columnFilters.map((filter) => {
            // Map the filter field to the correct relation name
            const fieldMap = {
              branch: "branch.name",
              group: "group.name",
              createdAt: "createdAt"
            };

            // Handle date range filter differently
            if (filter.id === "createdAt" && filter.value?.length === 2) {
              const [startDate, endDate] = filter.value;
              // Set end date to end of day
              const endOfDay = new Date(endDate);
              endOfDay.setHours(23, 59, 59, 999);

              return {
                field: fieldMap[filter.id],
                operator: "between",
                value: [
                  new Date(startDate).toISOString(),
                  endOfDay.toISOString()
                ]
              };
            }

            return {
              field: fieldMap[filter.id] || filter.id,
              operator: "in",
              value: filter.value?.map((v) => v?.value || v),
            };
          }),
        }),
        sortBy: sorting[0]?.id || "createdAt",
        order: sorting[0]?.desc ? "DESC" : "ASC",
      };

      console.log("Final API params:", params);

      const { data } = await api.get(`/members`, { params });
      console.log("API Response:", data);

      return {
        items: data?.results?.map((e) => {
          // Calculate shares bought (number of shares)
          let totalSharesBought = 0;
          let totalSharesAmount = 0;
          
          // Calculate from contributions
          if (e?.contributions && Array.isArray(e.contributions)) {
            e.contributions.forEach((contribution) => {
              if (contribution?.depositAmount) {
                // Try to get pricePerShare from contribution.group or contribution.groupMember.group
                const group = contribution?.group || contribution?.groupMember?.group;
                if (group?.pricePerShare) {
                  const shares = Math.floor(contribution.depositAmount / group.pricePerShare);
                  totalSharesBought += shares;
                  totalSharesAmount += shares * group.pricePerShare;
                }
              }
            });
          }
          
          // Calculate from groupMemberships if contributions don't have group data
          if (e?.groupMemberships && Array.isArray(e.groupMemberships)) {
            e.groupMemberships.forEach((gm) => {
              if (gm?.numberOfShares && gm?.group?.pricePerShare) {
                totalSharesBought += gm.numberOfShares;
                totalSharesAmount += gm.numberOfShares * gm.group.pricePerShare;
              }
            });
          }
          
          // Calculate unpaid loans and fines
          let unpaidLoansAndFines = 0;
          
          // Calculate unpaid loans (approved loans with remaining balance)
          if (e?.loans && Array.isArray(e.loans)) {
            e.loans.forEach((loan) => {
              if (loan?.status === "approved") {
                const totalPaid = loan?.payments?.reduce((sum, payment) => sum + (Number(payment?.amount) || 0), 0) || 0;
                const remaining = Number(loan?.amount || 0) - totalPaid;
                if (remaining > 0) {
                  unpaidLoansAndFines += remaining;
                }
              }
            });
          }
          
          // Add fines
          if (e?.fines && Array.isArray(e.fines)) {
            unpaidLoansAndFines += e.fines.reduce((sum, fine) => sum + (Number(fine?.amount) || 0), 0);
          }
          
          // Calculate uncontributed solidarity
          let uncontributedSolidarity = 0;
          if (e?.groupMemberships && Array.isArray(e.groupMemberships)) {
            e.groupMemberships.forEach((gm) => {
              if (gm?.group?.solidarityAmount) {
                const expectedSolidarity = Number(gm.group.solidarityAmount) || 0;
                // Get total solidarity contributed by this member in this group
                const memberContributions = e?.contributions?.filter(
                  (c) => {
                    const contributionGroupId = c?.group?.id || c?.groupMember?.group?.id;
                    return contributionGroupId === gm?.group?.id && Number(c?.solidarityAmount || 0) > 0;
                  }
                ) || [];
                const totalContributed = memberContributions.reduce(
                  (sum, c) => sum + Number(c?.solidarityAmount || 0),
                  0
                );
                const unpaid = expectedSolidarity - totalContributed;
                if (unpaid > 0) {
                  uncontributedSolidarity += unpaid;
                }
              }
            });
          }
          
          return {
          ...e,
          branch: e?.branch?.name,
          group: e?.group?.name,
          createdAt: e?.createdAt,
          // Use leaderRoles from backend for correct display
          leaderRoles: e?.leaderRoles || [],
          groupMemberships: e?.groupMemberships || [],
          contributions: e?.contributions || [],
          loans: e?.loans || [],
            fines: e?.fines || [],
            // Calculated fields
            sharesBought: totalSharesBought,
            sharesTotalAmount: totalSharesAmount,
            unpaidLoansAndFines: unpaidLoansAndFines,
            uncontributedSolidarity: uncontributedSolidarity,
          };
        }),
        totalPages: data?.totalPages,
      };
    },
  });

  // Add query for member details
  const memberDetailsQuery = useQuery(
    ["member-details", selectedMember?.id],
    async () => {
      if (!selectedMember?.id) return null;
      const { data } = await api.get(`/members/${selectedMember.id}`);
      return data.data;
    },
    {
      enabled: !!selectedMember?.id,
    }
  );

  const columns: ColumnDef<any>[] = [
    ...(user?.isAdmin ? [{
      id: "select",
      header: ({ table }) => (
        <Checkbox
          // @ts-ignore
          checked={
            table.getIsAllPageRowsSelected() ||
            (table.getIsSomePageRowsSelected() && "indeterminate")
          }
          onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
          aria-label={t("pages.members.selectAll")}
          className="translate-y-[2px]"
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          checked={row.getIsSelected()}
          onCheckedChange={(value) => row.toggleSelected(!!value)}
          aria-label={t("pages.members.selectRow")}
          className="translate-y-[2px]"
        />
      ),
      enableSorting: false,
      enableHiding: false,
    }] : []),
    {
      accessorKey: "id",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("pages.members.id")} />
      ),
      cell: ({ row }) => {
        return (
          <div className="flex items-center gap-3">
            #{row.getValue("id")}
          </div>
        );
      },
      enableSorting: true,
      enableHiding: false,
    },
    {
      accessorKey: "name",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("pages.members.member")} />
      ),
      cell: ({ row }) => {
        return (
          <div className="flex items-center gap-3">
            <Avatar className="h-8 w-8">
              <AvatarImage src={row.original.avatar} />
              <AvatarFallback>
                {row.original.firstName?.[0]}
                {row.original.lastName?.[0]}
              </AvatarFallback>
            </Avatar>
            <div>
              <button
                onClick={() => {
                  setSelectedMember(row.original);
                  setIsViewDialogOpen(true);
                }}
                className="font-medium hover:underline"
              >
                {row.original.firstName} {row.original.lastName}
              </button>

            </div>
          </div>
        );
      },
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: "leaderRoles",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("pages.members.leaderRoles")} />
      ),
      cell: ({ row }) => {
        const leaderRoles = row.original.leaderRoles || [];
        if (leaderRoles.length === 0) {
          return <span className="text-muted-foreground text-sm">{t("common.member")}</span>;
        }
        return (
          <div className="flex flex-wrap gap-1">
            {leaderRoles.map((leaderRole, index) => (
              <Badge
                key={index}
                variant="outline"
                className="text-xs px-1.5 py-0.5 bg-primary/10 text-primary hover:bg-primary/20"
              >
                {leaderRole.role} ({leaderRole.group.name})
              </Badge>
            ))}
          </div>
        );
      },
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: "phone",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("common.phone")} />
      ),
      cell: ({ row }) => {
        return (
          <div className="flex items-center gap-3">
            {row.getValue("phone")}
          </div>
        );
      },
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: "branch",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("pages.members.zone")} />
      ),
      cell: ({ row }) => {
        return (
          <div className="flex items-center gap-3">
            {row.original.branch?.name}
          </div>
        );
      },
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: "groups",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("pages.members.groups")} />
      ),
      cell: ({ row }) => {
        const groups = row.original.groups || [];

        if (groups.length === 0) {
          return <span className="text-muted-foreground text-sm">{t("pages.members.noGroups")}</span>;
        }

        return (
          <div className="flex flex-wrap gap-1">
            {groups.slice(0, 2).map((group, index) => (
              <Badge
                key={index}
                variant="outline"
                className="text-xs px-1.5 py-0.5"
              >
                {group.name}
              </Badge>
            ))}
            {groups.length > 2 && (
              <Badge variant="outline" className="text-xs px-1.5 py-0.5">
                {groups.length - 2} {t("pages.members.more")}
              </Badge>
            )}
          </div>
        );
      },
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: "currentSavings",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("pages.members.currentSavings")} />
      ),
      cell: ({ row }) => {
        return (
          <div className="flex items-center gap-3">
            {row.getValue("currentSavings")} FRW
          </div>
        );
      },
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: "sharesBought",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("pages.members.sharesBought")} />
      ),
      cell: ({ row }) => {
        const shares = row.original.sharesBought || 0;
        return (
          <div className="flex items-center gap-3">
            {shares}
          </div>
        );
      },
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: "sharesTotalAmount",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("pages.members.sharesTotalAmount")} />
      ),
      cell: ({ row }) => {
        const amount = row.original.sharesTotalAmount || 0;
        return (
          <div className="flex items-center gap-3">
            {Number(amount).toLocaleString()} FRW
          </div>
        );
      },
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: "unpaidLoansAndFines",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("pages.members.unpaidLoansAndFines")} />
      ),
      cell: ({ row }) => {
        const amount = row.original.unpaidLoansAndFines || 0;
        return (
          <div className="flex items-center gap-3">
            {Number(amount).toLocaleString()} FRW
          </div>
        );
      },
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: "uncontributedSolidarity",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("pages.members.uncontributedSolidarity")} />
      ),
      cell: ({ row }) => {
        const amount = row.original.uncontributedSolidarity || 0;
        return (
          <div className="flex items-center gap-3">
            {Number(amount).toLocaleString()} FRW
          </div>
        );
      },
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: "marriageStatus",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("pages.members.marriageStatus")} />
      ),
      cell: ({ row }) => {
        return (
          <div className="flex items-center gap-3">
            {row.getValue("marriageStatus")}
          </div>
        );
      },
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: "sourceOfIncome",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("pages.members.sourceOfIncome")} />
      ),
      cell: ({ row }) => {
        return (
          <div className="flex items-center gap-3">
            {row.getValue("sourceOfIncome")}
          </div>
        );
      },
      enableSorting: false,
      enableHiding: false,
    },
    // country
    {
      accessorKey: "country",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("pages.members.country")} />
      ),
      cell: ({ row }) => {
        return (
          <div className="flex items-center gap-3">
            {row.getValue("country")}
          </div>
        );
      },
      enableSorting: false,
      enableHiding: false,
    },
    {
      id: "actions",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("common.actions")} />
      ),
      cell: ({ row }) => (
        <div className="flex items-center justify-center mt-1 gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="h-7 w-8 p-0">
                <span className="sr-only">{t("pages.members.openMenu")}</span>
                <MoreVertical size={16} />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel>{t("common.actions")}</DropdownMenuLabel>
              <DropdownMenuItem
                onClick={() => {
                  setSelectedMember(row.original);
                  setIsViewDialogOpen(true);
                }}
              >
                {t("pages.members.viewDetails")}
              </DropdownMenuItem>
              {(user?.isAdmin || user?.role?.name === "President" || user?.role?.name === "Accountant" || user?.role?.name === "Secretary") && (
                <DropdownMenuItem
                  onClick={() => {
                    setRecordToEdit(row?.original);
                  }}
                >
                  {t("common.update")} {t("common.member")}
                </DropdownMenuItem>
              )}
              {user?.isAdmin && (
                <DropdownMenuItem
                  onClick={() => {
                    confirmModal.open({ meta: row?.original });
                  }}
                >
                  {t("common.delete")} {t("common.member")}
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      ),
    },
  ];

  const handleDelete = (record) => {
    confirmModal.setIsLoading(true);
    return api
      .delete(`/members/${record.id}`)
      .then(() => {
        recordsQuery.refetch();
        confirmModal.close();
        toast.success(t("messages.deletedSuccessfully"));
      })
      .catch((e) => {
        confirmModal.setIsLoading(false);
        const errorMessage = e.response?.data?.message || e.message;
        toast.error(errorMessage || "Failed to delete member");
      });
  };

  return (
    <>

      <ConfirmModal
        title={t("pages.members.deleteConfirmTitle")}
        description={t("pages.members.deleteConfirmDescription")}
        meta={confirmModal.meta}
        onConfirm={(meta) => {
          handleDelete(meta);
        }}
        isLoading={confirmModal.isLoading}
        open={confirmModal.isOpen}
        onClose={() => confirmModal.close()}
      />

      <Dialog open={isViewDialogOpen} onOpenChange={setIsViewDialogOpen}>
        <DialogContent className="max-w-4xl h-[80vh] overflow-hidden flex flex-col p-0">
          <DialogHeader className="border-b px-4 py-3">
            <DialogTitle className="text-lg font-semibold flex items-center gap-2">
              <span>{t("pages.members.memberDetails")}</span>
              {memberDetailsQuery.data && (
                <Badge variant="outline" className="text-xs">
                  ID: #{memberDetailsQuery.data.id}
                </Badge>
              )}
            </DialogTitle>
          </DialogHeader>

          {memberDetailsQuery.isLoading ? (
            <div className="flex items-center justify-center p-6 h-full">
              <Loader className="h-6 w-6 animate-spin" />
            </div>
          ) : memberDetailsQuery.data ? (
            <ScrollArea className="flex-1">
              <div className="p-4">
                {/* Compact Header */}
                <div className="mb-4 bg-card rounded-lg border p-3">
                  <div className="flex items-center gap-3">
                    <Avatar className="h-12 w-12">
                      <AvatarImage src={memberDetailsQuery.data.avatar} />
                      <AvatarFallback className="text-sm">
                        {memberDetailsQuery.data.firstName?.[0]}
                        {memberDetailsQuery.data.lastName?.[0]}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <h2 className="text-lg font-bold">{memberDetailsQuery.data.fullNames}</h2>
                      <p className="text-sm text-muted-foreground">{memberDetailsQuery.data.phone}</p>
                    </div>
                  </div>
                </div>

                <div className="space-y-6">
                  {/* Personal Information Table */}
                  <div>
                    <h3 className="text-sm font-semibold mb-2">{t("pages.members.personalInfo")}</h3>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-[200px]">{t("forms.gender")}</TableHead>
                          <TableHead>{t("pages.members.marriageStatus")}</TableHead>
                          <TableHead>{t("pages.members.sourceOfIncome")}</TableHead>
                          <TableHead>{t("pages.members.joinedDate")}</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        <TableRow>
                          <TableCell className="font-medium">{memberDetailsQuery.data.gender}</TableCell>
                          <TableCell className="font-medium">{memberDetailsQuery.data.marriageStatus}</TableCell>
                          <TableCell className="font-medium">{memberDetailsQuery.data.sourceOfIncome}</TableCell>
                          <TableCell className="font-medium">{new Date(memberDetailsQuery.data.joinedAt).toLocaleDateString()}</TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </div>

                  {/* Location Information Table */}
                  <div>
                    <h3 className="text-sm font-semibold mb-2">{t("pages.members.location")}</h3>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-[200px]">{t("pages.members.country")}</TableHead>
                          <TableHead>{t("pages.members.zone")}</TableHead>
                          <TableHead>{t("forms.currentAddress")}</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        <TableRow>
                          <TableCell className="font-medium">{memberDetailsQuery.data.country}</TableCell>
                          <TableCell className="font-medium">{memberDetailsQuery.data.branch?.name}</TableCell>
                          <TableCell className="font-medium">{memberDetailsQuery.data.currentAddress}</TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </div>

                  {/* Leader Roles Table */}
                  <div>
                    <h3 className="text-sm font-semibold mb-2">{t("pages.members.leaderRoles")}</h3>
                    {memberDetailsQuery.data.leaderRoles?.length > 0 ? (
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="w-[200px]">{t("forms.role")}</TableHead>
                            <TableHead>{t("common.group")}</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {memberDetailsQuery.data.leaderRoles.map((leaderRole, index) => (
                            <TableRow key={index}>
                              <TableCell>
                                <Badge
                                  variant="outline"
                                  className="text-xs px-2 py-1 bg-primary/10 text-primary"
                                >
                                  {leaderRole.role}
                                </Badge>
                              </TableCell>
                              <TableCell className="font-medium">{leaderRole.group.name}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    ) : (
                      <div className="text-center py-4 text-muted-foreground text-sm border rounded-md">
                        {t("pages.members.noLeaderRoles")}
                      </div>
                    )}
                  </div>

                  {/* Group Memberships */}
                  <div>
                    <h3 className="text-sm font-semibold mb-2">{t("pages.members.groups")}</h3>
                    {memberDetailsQuery.data.groupMemberships?.length > 0 ? (
                      <div className="space-y-3">
                        {memberDetailsQuery.data.groupMemberships?.map((membership) => {
                          const memberShares = memberDetailsQuery.data.contributions?.length > 0
                            ? Math.floor(memberDetailsQuery.data.contributions[0].depositAmount / membership.group.pricePerShare)
                            : 0;

                          return (
                            <div key={membership.id} className="bg-card border rounded-lg p-3">
                              <div className="flex items-center justify-between mb-2">
                                <h4 className="font-medium">{membership.group.name}</h4>
                              </div>
                              <div className="grid grid-cols-3 gap-2 text-xs">
                                <div>
                                  <span className="text-muted-foreground">{t("pages.members.shares")}:</span>
                                  <span className="font-medium ml-1">{memberShares}</span>
                                </div>
                                <div>
                                  <span className="text-muted-foreground">{t("pages.members.value")}:</span>
                                  <span className="font-medium ml-1">{membership.group.pricePerShare?.toLocaleString()} FRW</span>
                                </div>
                                <div>
                                  <span className="text-muted-foreground">{t("pages.members.meeting")}:</span>
                                  <span className="font-medium ml-1">{membership.group.meetingDay || t("pages.members.notSet")}</span>
                                </div>
                              </div>
                              <div className="text-xs text-muted-foreground mt-1">
                                {membership.group.meetingLocation}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="text-center py-4 text-muted-foreground text-sm border rounded-md">
                        {t("pages.members.noGroupMemberships")}
                      </div>
                    )}
                  </div>

                  {/* Branch Information */}
                  <div>
                    <h3 className="text-sm font-semibold mb-2">{t("pages.members.branchInformation")}</h3>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-[200px]">{t("pages.members.sectorName")}</TableHead>
                          <TableHead>{t("pages.members.sectorAddress")}</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        <TableRow>
                          <TableCell className="font-medium">{memberDetailsQuery.data.branch?.name}</TableCell>
                          <TableCell className="font-medium">{memberDetailsQuery.data.branch?.address}</TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </div>
                </div>
              </div>
            </ScrollArea>
          ) : (
            <div className="p-6 text-center text-muted-foreground h-full flex items-center justify-center">
              <div>
                <p className="mb-2">{t("pages.members.noMemberDetails")}</p>
                <Button variant="outline" size="sm" onClick={() => setIsViewDialogOpen(false)}>
                  {t("pages.members.close")}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <div className="container mx-auto py-10">
        <div className="flex justify-between items-center mb-4">
          <h1 className="text-2xl font-bold">{t("pages.members.title")}</h1>
          <div className="flex items-center gap-2">
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    "justify-start text-left font-normal",
                    !dateRange && "text-muted-foreground"
                  )}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {dateRange?.from ? (
                    dateRange.to ? (
                      <>
                        {format(dateRange.from, "LLL dd, y")} -{" "}
                        {format(dateRange.to, "LLL dd, y")}
                      </>
                    ) : (
                      format(dateRange.from, "LLL dd, y")
                    )
                  ) : (
                    <span>{t("pages.members.pickDateRange")}</span>
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  initialFocus
                  mode="range"
                  defaultMonth={dateRange?.from}
                  selected={dateRange}
                  onSelect={handleDateRangeChange}
                  numberOfMonths={2}
                />
              </PopoverContent>
            </Popover>
            <Button
              variant="outline"
              onClick={async () => {
                // Dynamically import jsPDF and html2canvas
                const jsPDF = (await import('jspdf')).jsPDF;
                const html2canvas = (await import('html2canvas')).default;
                // Create a hidden div for PDF content
                const pdfDiv = document.createElement('div');
                pdfDiv.style.position = 'fixed';
                pdfDiv.style.left = '-9999px';
                pdfDiv.style.top = '0';
                pdfDiv.style.width = '900px';
                pdfDiv.style.background = '#fff';
                pdfDiv.style.fontFamily = 'Arial, sans-serif';
                pdfDiv.innerHTML = `
                  <div style="padding:32px;">
                    <div style="display:flex;justify-content:space-between;font-size:13px;margin-bottom:8px;">
                      <span>${new Date().toLocaleString()}</span>
                      <span>ikimina management system.</span>
                    </div>
                    <div style="text-align:center;margin-bottom:8px;font-size:16px;font-weight:600;">ikimina | ${t("pages.members.membersReport")}</div>
                    <div style="text-align:center;margin-bottom:16px;font-size:22px;font-weight:bold;">${t("pages.members.membersList")}</div>
                    <hr style="margin-bottom:16px;" />
                    <table style="width:100%;border-collapse:collapse;font-size:13px;">
                      <thead>
                        <tr style="background:#f5f5f5;">
                          <th style="border:1px solid #ddd;padding:6px;">ID</th>
                          <th style="border:1px solid #ddd;padding:6px;">Member</th>
                          <th style="border:1px solid #ddd;padding:6px;">Leader Roles</th>
                          <th style="border:1px solid #ddd;padding:6px;">Phone</th>
                          <th style="border:1px solid #ddd;padding:6px;">Sector</th>
                          <th style="border:1px solid #ddd;padding:6px;">Groups</th>
                          <th style="border:1px solid #ddd;padding:6px;">Current Savings</th>
                          <th style="border:1px solid #ddd;padding:6px;">Marriage Status</th>
                          <th style="border:1px solid #ddd;padding:6px;">Source of Income</th>
                          <th style="border:1px solid #ddd;padding:6px;">Country</th>
                        </tr>
                      </thead>
                      <tbody>
                        ${(recordsQuery.data?.items || []).map(m => `
                          <tr>
                            <td style="border:1px solid #ddd;padding:6px;">${m.id}</td>
                            <td style="border:1px solid #ddd;padding:6px;">${m.fullNames}</td>
                            <td style="border:1px solid #ddd;padding:6px;">${m.role}</td>
                            <td style="border:1px solid #ddd;padding:6px;">${m.phone}</td>
                            <td style="border:1px solid #ddd;padding:6px;">${m.branch?.name || ''}</td>
                            <td style="border:1px solid #ddd;padding:6px;">${(m.groups || []).map(g => g.name).join('; ')}</td>
                            <td style="border:1px solid #ddd;padding:6px;">${m.currentSavings} FRW</td>
                            <td style="border:1px solid #ddd;padding:6px;">${m.marriageStatus}</td>
                            <td style="border:1px solid #ddd;padding:6px;">${m.sourceOfIncome}</td>
                            <td style="border:1px solid #ddd;padding:6px;">${m.country}</td>
                          </tr>
                        `).join('')}
                      </tbody>
                    </table>
                    <div style="margin-top:24px;display:flex;justify-content:space-between;font-size:14px;">
                      <div><b>${t("pages.members.totalMembers")}:</b> ${(recordsQuery.data?.items || []).length}</div>
                      <div><b>${t("pages.members.generatedOn")}</b> ${new Date().toLocaleString()}</div>
                    </div>
                  </div>
                `;
                document.body.appendChild(pdfDiv);
                // Use html2canvas to render the div
                const canvas = await html2canvas(pdfDiv, { scale: 2 });
                const imgData = canvas.toDataURL('image/png');
                const pdf = new jsPDF({ orientation: 'landscape', unit: 'px', format: [canvas.width, canvas.height] });
                pdf.addImage(imgData, 'PNG', 0, 0, canvas.width, canvas.height);
                pdf.save('members_report.pdf');
                document.body.removeChild(pdfDiv);
              }}
            >
              {t("pages.members.downloadMembersReport")}
            </Button>
            {(user?.isAdmin || user?.role?.name === "President" || user?.role?.name === "Accountant" || user?.role?.name === "Secretary") && (
              <>
                <Button variant="outline" onClick={() => setBulkImportOpen(true)}>
                  {t("common.bulkImport")} {t("pages.members.title")}
                </Button>
                <Button onClick={() => newRecordModal.open()}>
                  <PlusCircle size={16} className="mr-2" />
                  {t("pages.members.addMember")}
                </Button>
              </>
            )}
          </div>
        </div>

        <DataTable
          title={t("pages.members.title")}
          columns={columns}
          data={recordsQuery.data?.items || []}
          facets={[
            {
              name: "branch",
              title: t("pages.members.selectSector"),
              type: "select",
              options: branches,
            },
            {
              name: "gender",
              title: t("pages.members.selectGender"),
              type: "select",
              options: genderOptions,
            },
            {
              name: "marriageStatus",
              title: t("pages.members.selectMarriageStatus"),
              type: "select",
              options: marriageStatusOptions,
            },
            {
              name: "sourceOfIncome",
              title: t("pages.members.selectSourceOfIncome"),
              type: "select",
              options: sourceOfIncomeOptions,
            }
          ]}
          isLoading={recordsQuery.status === "loading"}
          defaultColumnVisibility={{}}
          onSearch={(value) => setSearchText(value)}
          columnFilters={columnFilters}
          setColumnFilters={setColumnFilters}
          setSorting={setSorting}
          sorting={sorting}
          setPagination={setPagination}
          pageIndex={pageIndex}
          pageSize={pageSize}
          pageCount={recordsQuery?.data?.totalPages || 0}
          isFetching={recordsQuery.isFetching}
        />
      </div>

      <MemberForm
        isOpen={newRecordModal.isOpen || Boolean(recordToEdit)}
        setIsOpen={(e) => {
          newRecordModal.setisOpen(e);
          if (!e) {
            setRecordToEdit(undefined);
          }
        }}
        refetch={recordsQuery.refetch}
        record={recordToEdit}
      />
      {/* Place Bulk Upload Members button on the main actions row, next to Add Member */}
      {/* Removed duplicate Bulk Upload Members button below the table */}
      <BulkImport
        open={bulkImportOpen}
        setOpen={setBulkImportOpen}
        sample={sampleMember}
        name="members"
        endPoint="/members/import"
        onComplete={() => {
          window.location.reload();
        }}
      />
    </>
  );
}